import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Box } from '@indeed/ifl-components';
import { GettextInterface } from '@indeed/indeed-gettext';
import ContactInfoForm from '../../../components/ContactInfoForm/ContactInfoForm';
import JobDescription from '../../../components/JobDescription/JobDescription';
import PageTransition from '../../../components/PageTransition/PageTransition';
import { usePageFlow } from '../../../lib/pageFlow/usePageFlow';
import {
    useGetDefaultApplyContactInfoQuery,
    useUpdateDefaultApplyContactInfoMutation,
} from './__generated__/ContactInfoPage.generated';

export interface ContactInfoPageProps {
    step?: string;
    gt?: GettextInterface;
}

// Contact info form data structure matching ContactInfoForm expectations
// Adapted from OneGraph schema - removing fields not available in OneGraph
interface ContactInfoData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string; // Maps to phoneNumber in OneGraph
    address: {
        street: string; // Not available in OneGraph - will be empty
        city: string; // Available in OneGraph
        state: string; // Maps to area in OneGraph
        zipCode: string; // Not available in OneGraph - will be empty
        country: string; // Available in OneGraph
    };
}

const ContactInfoPage: React.FunctionComponent<ContactInfoPageProps> = () => {
    // Page-level flow management with flow-aware navigation
    const { goForward, goBack, canGoBack } = usePageFlow();

    // Page-level state for coordination
    const [pageState, setPageState] = useState({
        hasUnsavedChanges: false,
        submitError: null as string | null,
        isSubmitting: false,
    });

    // Apollo data fetching at page level using OneGraph schema
    // Draft node ID - in a real app, this would come from URL params or context
    const draftNodeId = 'default_apply_state_123';

    const {
        data: contactInfoData,
        loading: profileLoading,
        error: profileError,
    } = useGetDefaultApplyContactInfoQuery({
        variables: { nodeId: draftNodeId },
        errorPolicy: 'all',
    });

    const [updateContactInfo, { loading: updateLoading }] =
        useUpdateDefaultApplyContactInfoMutation();

    // Handle unsaved changes warning
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (pageState.hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [pageState.hasUnsavedChanges]);

    // Page-level form submission handler with flow-aware navigation - memoized
    const handleFormSubmit = useCallback(
        async (formData: ContactInfoData): Promise<void> => {
            setPageState((prev) => ({ ...prev, isSubmitting: true, submitError: null }));

            try {
                await updateContactInfo({
                    variables: {
                        input: {
                            id: draftNodeId,
                        },
                        updateContactInfoInput: {
                            firstName: formData.firstName,
                            lastName: formData.lastName,
                            email: formData.email,
                            phoneNumber: formData.phone, // Map phone to phoneNumber
                            area: formData.address.state, // Map address.state to area
                            city: formData.address.city,
                            country: formData.address.country,
                            // Note: street and zipCode are not available in OneGraph
                            // firstNamePronunciation and lastNamePronunciation removed from form
                        },
                    },
                });

                // Success: update state and navigate to next step
                setPageState((prev) => ({
                    ...prev,
                    hasUnsavedChanges: false,
                    isSubmitting: false,
                }));

                // Use flow-aware navigation to go to next step
                await goForward();
            } catch (error) {
                console.error('❌ Page: Submit failed:', error);
                setPageState((prev) => ({
                    ...prev,
                    submitError: error instanceof Error ? error.message : 'An error occurred',
                    isSubmitting: false,
                }));
            }
        },
        [updateContactInfo, goForward, draftNodeId],
    );

    // Handle form data changes - memoized to prevent infinite re-renders
    const handleFormChange = useCallback((hasChanges: boolean) => {
        setPageState((prev) => ({ ...prev, hasUnsavedChanges: hasChanges }));
    }, []);

    // Handle flow-aware back navigation with unsaved changes check - memoized
    const handleBack = useCallback(() => {
        if (pageState.hasUnsavedChanges) {
            const confirmed = window.confirm(
                'You have unsaved changes. Are you sure you want to leave?',
            );
            if (!confirmed) return;
        }
        void goBack(); // Use flow-aware back navigation
    }, [pageState.hasUnsavedChanges, goBack]);

    return (
        <PageTransition pageKey="contact-info">
            <Helmet>
                <title>Contact Information - Indeed</title>
                <meta
                    name="description"
                    content="Provide your contact information to complete your job application with Indeed."
                />
                <meta
                    name="keywords"
                    content="contact information, job application, Indeed, careers"
                />
            </Helmet>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: ['1fr', '1fr 1fr'],
                    gap: 6,
                    alignItems: 'start',
                }}
            >
                {/* Left Section - Contact Form */}
                <Box>
                    <ContactInfoForm
                        // Pass down data and handlers
                        profileData={contactInfoData}
                        profileLoading={profileLoading}
                        profileError={profileError}
                        isSubmitting={updateLoading || pageState.isSubmitting}
                        submitError={pageState.submitError}
                        onSubmit={handleFormSubmit}
                        onChange={handleFormChange}
                        onBack={handleBack}
                        hasUnsavedChanges={pageState.hasUnsavedChanges}
                        canGoBack={canGoBack}
                    />
                </Box>

                {/* Right Section - Job Description */}
                <Box>
                    <JobDescription />
                </Box>
            </Box>
        </PageTransition>
    );
};

export default ContactInfoPage;
